import { io, Socket } from 'socket.io-client';
import { getStoredUser } from './auth';
import {
  SIGNUS_API_URL,
  WSS_PATH,
  WORD_DISCUSS_WSS_NAMESPACE,
  WORD_DISCUSS_WSS_EVENT,
} from './constants';
import { ApiError, extractErrorMessage, extractErrorType, fetchWithTokenRefresh } from './apiFetch';

/* global console */

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

interface WordDiscussChunkPayload {
  sessionId: string;
  assistantMessageId: string;
  chunkMessage: string;
  chunkIndex: number;
  isFinal: boolean;
}

interface WordDiscussErrorPayload {
  sessionId: string;
  assistantMessageId: string;
}

interface WssMessage {
  payload: WordDiscussChunkPayload | WordDiscussErrorPayload;
  payloadType?: string;
}

async function apiPost<T>(path: string, body: unknown, accessToken: string): Promise<T> {
  const res = await fetchWithTokenRefresh(`${SIGNUS_API_URL}${path}`, accessToken, token => ({
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: token },
    body: JSON.stringify(body),
  }));

  const json = await res.json();

  if (!res.ok) {
    throw new ApiError(
      extractErrorMessage(json, `Request failed (${res.status})`),
      extractErrorType(json),
    );
  }

  return (json.payload ?? json) as T;
}

async function createSession(
  teamSlug: string,
  documentText: string,
  accessToken: string,
): Promise<string> {
  const data = await apiPost<{ sessionId: string }>(
    `/v0/word/teams/${encodeURIComponent(teamSlug)}/discuss/sessions`,
    { documentText },
    accessToken,
  );
  if (!data.sessionId) throw new Error('Server did not return a sessionId');
  return data.sessionId;
}

async function sendMessage(
  teamSlug: string,
  sessionId: string,
  text: string,
  accessToken: string,
  documentText?: string,
): Promise<{ userMessageId: string; assistantMessageId: string }> {
  const body: Record<string, string> = { text };
  if (documentText !== undefined) body.documentText = documentText;
  return apiPost(
    `/v0/word/teams/${encodeURIComponent(teamSlug)}/discuss/sessions/${encodeURIComponent(sessionId)}/messages`,
    body,
    accessToken,
  );
}

function streamAssistantResponse(
  sessionId: string,
  assistantMessageId: string,
  accessToken: string,
  teamSlug: string,
  onChunk: (text: string) => void,
  signal: AbortSignal | undefined,
  timeoutMs = 120_000,
): Promise<string> {
  const wssUrl = `${SIGNUS_API_URL}/wss${WORD_DISCUSS_WSS_NAMESPACE}`;

  return new Promise((resolve, reject) => {
    let settled = false;
    let acc = '';

    const socket: Socket = io(wssUrl, {
      path: WSS_PATH,
      transports: ['websocket'],
      auth: { token: getStoredUser()?.accessToken ?? accessToken, teamSlug, sessionId },
    });

    const cleanup = (finalAcc: string): void => {
      settled = true;
      clearTimeout(timer);
      socket.disconnect();
      resolve(finalAcc);
    };

    const fail = (err: Error): void => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      socket.disconnect();
      reject(err);
    };

    const timer = setTimeout(() => {
      fail(new Error('Response timed out. Please try again.'));
    }, timeoutMs);

    signal?.addEventListener('abort', () => {
      if (!settled) cleanup(acc);
    });

    socket.on('connect_error', (err: Error) => {
      fail(new Error(`WebSocket connection failed: ${err.message}`));
    });

    socket.on(WORD_DISCUSS_WSS_EVENT, (msg: WssMessage) => {
      if (settled) return;

      const p = msg?.payload as WordDiscussChunkPayload;
      if (!p || p.sessionId !== sessionId || p.assistantMessageId !== assistantMessageId) return;

      if (msg.payloadType === 'WordDiscussSessionMessageErrorDto') {
        fail(new Error('Sigi encountered an error generating the response.'));
        return;
      }

      if (p.chunkMessage) {
        acc += p.chunkMessage;
        onChunk(p.chunkMessage);
      }

      if (p.isFinal) {
        cleanup(acc);
      }
    });
  });
}

export interface SendDiscussOpts {
  sessionId: string | null;
  /** The document text as of this send. Always uploaded to keep S3 metadata current. */
  documentText: string;
  teamSlug: string;
  accessToken: string;
  message: string;
  onChunk: (text: string) => void;
  signal?: AbortSignal;
}

export interface SendDiscussResult {
  text: string;
  sessionId: string;
}

/**
 * Sends a message to the Signus word discuss backend and streams the response.
 * Creates a new session on the first message (sessionId is null).
 * Always passes documentText so the S3 metadata stays current with the latest document content.
 */
export async function sendDiscussMessage(opts: SendDiscussOpts): Promise<SendDiscussResult> {
  let { sessionId } = opts;

  if (!sessionId) {
    // First message — create session (uploads document text), then send message.
    sessionId = await createSession(opts.teamSlug, opts.documentText, opts.accessToken);
    const { assistantMessageId } = await sendMessage(
      opts.teamSlug,
      sessionId,
      opts.message,
      opts.accessToken,
    );
    const text = await streamAssistantResponse(
      sessionId,
      assistantMessageId,
      opts.accessToken,
      opts.teamSlug,
      opts.onChunk,
      opts.signal,
    );
    return { text, sessionId };
  }

  // Existing session — always pass documentText to keep S3 metadata current.
  const { assistantMessageId } = await sendMessage(
    opts.teamSlug,
    sessionId,
    opts.message,
    opts.accessToken,
    opts.documentText,
  );

  const text = await streamAssistantResponse(
    sessionId,
    assistantMessageId,
    opts.accessToken,
    opts.teamSlug,
    opts.onChunk,
    opts.signal,
  );

  return { text, sessionId };
}
